import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getStrategies, {FeedType} from '../../../../utils/getStrategies';

const inputSchema = z.object({
  feedType: z.nativeEnum(FeedType).describe('The feed type the user is working with'),
});

const responseSchema = z.object({
  formattedStrategies: z.string(),
});

function getSelectionStrategiesTool(store: CLStore) {
  async function execute({
    input: {feedType},
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolResult('Campaign not found');
      }

      const campaignType = campaign.type;
      const strategies = getStrategies(feedType, campaignType);

      const catalogue = strategies
        .map((s) => {
          return `ID: ${s.strategyId}
Name: ${s.name}
Description: ${s.description}`;
        })
        .join('\n\n');

      const systemPrompt = `You are a helpful assistant.
Summarize the strategies from the catalogue below.
Format the output as follows:

=== ${feedType} Selection Strategies ===
[Strategy Name] - [Strategy Description]
[Strategy Name] - [Strategy Description]
...

The description should be 1-2 short sentences summarized from the full description which precisely and concisely summarize the description.

Strategies catalogue:
${catalogue}`;

      const result = await toolUtils.invokeStandaloneLLMCall({
        messages: [openAISystemMessage(systemPrompt)],
        responseSchema,
        model: getSetupAgentModel(),
        callChain,
      });

      return toolResult(result.formattedStrategies);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolResult(`Error retrieving strategies: ${message}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetSelectionStrategies,
    displayName: 'What strategies can I use?',
    description:
      'Retrieves the supported strategies for Product, Location or Content selection cards.',
    execute,
    inputSchema,
  });
}

export default getSelectionStrategiesTool;
